import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  Tooltip,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Sidebar from '../components/Sidebar';
import { getAllDepartments, addDepartment, updateDepartment, deleteDepartment } from '../api/services';

// ---------------------------------------------------------------
// Shared look (design tokens + small UI helpers) - same as Earnings
// ---------------------------------------------------------------
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  radius: '16px',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-disabled': { backgroundColor: '#f3f5f7' },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: '#fff',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': { backgroundColor: ui.primaryDark, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
};

const outlineButtonSx = (color, soft) => ({
  flex: 1,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  color,
  borderColor: color,
  '&:hover': { borderColor: color, backgroundColor: soft },
  '&.Mui-disabled': { color: '#b0b7be', borderColor: '#e0e4e8' },
});

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: '0.8rem',
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: '0.875rem',
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: '12px',
  p: 2,
  backgroundColor: '#fff',
};

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 'bold',
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: '1.5rem', sm: '1.85rem', md: '2.125rem' },
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          backgroundColor: ui.primary,
          height: '4px',
          width: { xs: '80px', sm: '100px' },
          position: 'absolute',
          left: 0,
          bottom: '-8px',
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const MenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: 'block', md: 'none' },
      position: 'sticky',
      top: { xs: '64px', sm: '80px' },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: 'linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
          color: '#fff',
          borderRadius: '12px',
          p: 1.2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            background: 'linear-gradient(135deg, #007a8a 0%, #005b66 100%)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: '1.6rem' }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: '#fff',
      boxShadow: ui.shadow,
      overflow: 'visible',
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const StatCard = ({ label, value, icon: Icon }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 2,
      minWidth: 0,
      borderRadius: '14px',
      border: `1px solid ${ui.border}`,
      backgroundColor: '#fff',
      boxShadow: ui.shadow,
    }}
  >
    {Icon && (
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: '10px',
          backgroundColor: ui.primarySoft,
          color: ui.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon fontSize="small" />
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: ui.muted }} noWrap>
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          color: ui.text,
          fontSize: { xs: '1.05rem', md: '1.2rem' },
          lineHeight: 1.3,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const EmptyState = ({ icon: Icon = InboxOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography variant="body2" sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box role="status" aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const DepartmentManagement = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [departments, setDepartments] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [actionType, setActionType] = useState('');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const response = await getAllDepartments();
        console.log('Full departments response:', response);
        
        // Try multiple possible data structures
        let departmentsData = [];
        
        // Check if response has data.content structure (most common)
        if (response?.data?.content && Array.isArray(response.data.content)) {
          departmentsData = response.data.content;
        } 
        // Check if response is directly an array
        else if (Array.isArray(response)) {
          departmentsData = response;
        }
        // Check if response has a data property that's an array
        else if (response?.data && Array.isArray(response.data)) {
          departmentsData = response.data;
        }
        // Check if response has a content property that's an array
        else if (response?.content && Array.isArray(response.content)) {
          departmentsData = response.content;
        }
        // If it's a single department object
        else if (response && typeof response === 'object' && response.id) {
          departmentsData = [response];
        }
        
        console.log('Extracted departmentsData:', departmentsData);
        
        // Set departments and total records
        setDepartments(departmentsData);
        setTotalRecords(departmentsData.length);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setSnackbarMessage('Failed to fetch departments');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setDepartments([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  // Handles pagination page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Closes snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Submits new department data
  const handleSubmit = async () => {
    if (name && description) {
      setLoading(true);
      try {
        const newDepartment = { name, description };
        const addedDepartment = await addDepartment(newDepartment);
        // Refresh the list after adding
        const response = await getAllDepartments();
        let departmentsData = [];
        if (response?.data?.content && Array.isArray(response.data.content)) {
          departmentsData = response.data.content;
        } else if (Array.isArray(response)) {
          departmentsData = response;
        }
        setDepartments(departmentsData);
        setTotalRecords(departmentsData.length);
        setSnackbarMessage('Department added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setName('');
        setDescription('');
      } catch (error) {
        console.error('Error adding department:', error);
        setSnackbarMessage('Failed to add department');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    } else {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  // Opens modal for edit or delete actions
  const handleOpenModal = (department, type) => {
    setSelectedDepartment(department);
    setActionType(type);
    if (type === 'Edit') {
      setEditName(department.name);
      setEditDescription(department.description);
    }
    setOpenModal(true);
  };

  // Closes modal and resets state
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDepartment(null);
    setActionType('');
    setEditName('');
    setEditDescription('');
  };

  // Confirms edit or delete actions
  const handleConfirmAction = async () => {
    if (actionType === 'Delete') {
      setLoading(true);
      try {
        await deleteDepartment(selectedDepartment.id);
        // Refresh the list after deletion
        const response = await getAllDepartments();
        let departmentsData = [];
        if (response?.data?.content && Array.isArray(response.data.content)) {
          departmentsData = response.data.content;
        } else if (Array.isArray(response)) {
          departmentsData = response;
        }
        setDepartments(departmentsData);
        setTotalRecords(departmentsData.length);
        setSnackbarMessage('Department deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error deleting department:', error);
        setSnackbarMessage('Failed to delete department');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    } else if (actionType === 'Edit') {
      if (editName && editDescription) {
        setLoading(true);
        try {
          await updateDepartment(selectedDepartment.id, {
            name: editName,
            description: editDescription,
          });
          // Refresh the list after update
          const response = await getAllDepartments();
          let departmentsData = [];
          if (response?.data?.content && Array.isArray(response.data.content)) {
            departmentsData = response.data.content;
          } else if (Array.isArray(response)) {
            departmentsData = response;
          }
          setDepartments(departmentsData);
          setTotalRecords(departmentsData.length);
          setSnackbarMessage('Department updated successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } catch (error) {
          console.error('Error updating department:', error);
          setSnackbarMessage('Failed to update department');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        } finally {
          setLoading(false);
        }
      } else {
        setSnackbarMessage('Please fill in all required fields');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }
    }
    handleCloseModal();
  };

  // Toggles mobile sidebar
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Paginated data for display - ensure departments is an array
  const paginatedDepartments = Array.isArray(departments) 
    ? departments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const renderActions = (department, withLabels) => {
    if (withLabels) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => handleOpenModal(department, 'Edit')}
            disabled={loading}
            startIcon={<EditOutlinedIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.primary, ui.primarySoft)}
          >
            Edit
          </Button>
          <Button
            onClick={() => handleOpenModal(department, 'Delete')}
            disabled={loading}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            variant="outlined"
            size="small"
            sx={outlineButtonSx(ui.danger, ui.dangerSoft)}
          >
            Delete
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Edit">
          <span>
            <IconButton
              onClick={() => handleOpenModal(department, 'Edit')}
              disabled={loading}
              size="small"
              aria-label={`Edit ${department.name}`}
              sx={{ color: ui.primary, '&:hover': { backgroundColor: ui.primarySoft } }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              onClick={() => handleOpenModal(department, 'Delete')}
              disabled={loading}
              size="small"
              aria-label={`Delete ${department.name}`}
              sx={{ color: ui.danger, '&:hover': { backgroundColor: ui.dangerSoft } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '50px', sm: '60px', md: '120px' },
            backgroundColor: '#fff',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          <MenuButton onClick={handleDrawerToggle} />

          <PageHeader title="Department Management" />

          <StatGrid>
            <StatCard
              label="Total departments"
              value={totalRecords.toLocaleString()}
              icon={ApartmentOutlinedIcon}
            />
          </StatGrid>

          {/* ------------------------------------------------------ */}
          {/* Add department form                                    */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="Add department">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 2.5,
              }}
            >
              <TextField
                label="Name*"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={loading}
              />
              <TextField
                label="Description*"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                sx={fieldSx}
                disabled={loading}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'flex-end',
                  gap: 2,
                  pt: 2.5,
                  borderTop: `1px solid ${ui.border}`,
                }}
              >
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading || !name || !description}
                  startIcon={
                    loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : null
                  }
                  sx={primaryButtonSx}
                >
                  {loading ? 'Adding...' : 'Add department'}
                </Button>
              </Box>
            </Box>
          </SectionCard>

          {/* ------------------------------------------------------ */}
          {/* Department list                                        */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="Department list">
            {loading ? (
              <ListSkeleton rows={rowsPerPage > 5 ? 6 : rowsPerPage} />
            ) : !Array.isArray(departments) || departments.length === 0 ? (
              <EmptyState
                icon={ApartmentOutlinedIcon}
                title="No departments found"
                message="Departments you add will show up here."
              />
            ) : (
              <>
                {/* Desktop / tablet table */}
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    overflowX: 'auto',
                    border: `1px solid ${ui.border}`,
                    borderRadius: '12px',
                  }}
                >
                  <Table sx={{ minWidth: 480 }}>
                    <TableHead>
                      <TableRow>
                        {['Name', 'Description', 'Actions'].map((h) => (
                          <TableCell key={h} sx={headCellSx}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDepartments.map((department) => (
                        <TableRow
                          key={department.id}
                          hover
                          sx={{ '&:last-child td': { borderBottom: 0 } }}
                        >
                          <TableCell
                            sx={{ ...bodyCellSx, fontWeight: 600, wordBreak: 'break-word' }}
                          >
                            {department.name}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                            {department.description}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{renderActions(department, false)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* Mobile cards */}
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {paginatedDepartments.map((department) => (
                    <Box key={department.id} sx={mobileCardSx}>
                      <Box sx={{ mb: 1.5, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: ui.text, wordBreak: 'break-word' }}>
                          {department.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word' }}>
                          {department.description}
                        </Typography>
                      </Box>
                      {renderActions(department, true)}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                mt: 2,
                color: ui.text,
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  px: 0,
                },
                '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
              }}
            />
          </SectionCard>
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      {/* ---------------------------------------------------------- */}
      {/* Edit / Delete dialog                                       */}
      {/* ---------------------------------------------------------- */}
      <Dialog
        open={openModal}
        onClose={loading ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 4 } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontWeight: 700,
            color: ui.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: actionType === 'Delete' ? ui.dangerSoft : ui.primarySoft,
              color: actionType === 'Delete' ? ui.danger : ui.primary,
            }}
          >
            {actionType === 'Delete' ? (
              <WarningAmberRoundedIcon fontSize="small" />
            ) : (
              <EditOutlinedIcon fontSize="small" />
            )}
          </Box>
          {actionType === 'Edit' ? 'Edit department' : 'Delete department'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {actionType === 'Delete' ? (
            <Box>
              <Typography sx={{ mb: 2, color: ui.text }}>
                Are you sure you want to delete the department{' '}
                <strong>"{selectedDepartment?.name}"</strong>? This action cannot be undone.
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: ui.surface,
                  border: `1px solid ${ui.border}`,
                }}
              >
                <Typography variant="body2" sx={{ color: ui.muted }}>
                  Description
                </Typography>
                <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                  {selectedDepartment?.description}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
              <TextField
                label="Name*"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                variant="outlined"
                fullWidth
                sx={fieldSx}
                disabled={loading}
              />
              <TextField
                label="Description*"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                sx={fieldSx}
                disabled={loading}
              />
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            disabled={loading}
            sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null
            }
            sx={{
              ...primaryButtonSx,
              py: 1,
              backgroundColor: actionType === 'Edit' ? ui.primary : ui.danger,
              '&:hover': {
                backgroundColor: actionType === 'Edit' ? ui.primaryDark : '#b71c1c',
              },
              '&.Mui-disabled': { backgroundColor: '#d5dbe0', color: '#8a949e' },
            }}
          >
            {loading ? 'Processing...' : actionType === 'Edit' ? 'Save changes' : 'Delete department'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartmentManagement;